import { User } from './user-model';

export enum EventStream {
  System = 'system',
  Unknown = 'unknown',
  Test = 'test',
  User = 'user',
  Token = 'token',
  List = 'list',
  Index = 'index',
  Item = 'item',
  Identity = 'identity',
  IdentityGroup = 'identity-group',
  Setting = 'setting',
  Subscription = 'subscription',
}

export const EventStreamQueryParam = {
  encode: (value?: EventStream | null | undefined): string | undefined =>
    value ? (value as string) : undefined,

  decode: (
    value?: string | (string | null | undefined)[] | null | undefined
  ): EventStream | undefined =>
    value && Object.values(EventStream).includes(value as any)
      ? (value as EventStream)
      : undefined,
};

export enum EventType {
  System = 'system',
  Unknown = 'unknown',
  Test = 'test',
  UserCreated = 'user-created',
  UserUpdated = 'user-updated',
  UserDeleted = 'user-deleted',
  UserDeletedSelf = 'user-deleted-self',
  UserRegistered = 'user-registered',
  UserForgotPassword = 'user-forgot-password',
  UserChangePassword = 'user-change-password',
  TokenCreated = 'token-created',
  TokenUpdated = 'token-updated',
  TokenRegenerated = 'token-regenerated',
  TokenDeleted = 'token-deleted',
  ListCreated = 'list-created',
  ListUpdated = 'list-updated',
  ListDeleted = 'list-deleted',
  IndexCreated = 'index-created',
  IndexUpdated = 'index-updated',
  IndexDeleted = 'index-deleted',
  IndexBuilt = 'index-built',
  IndexBuildFailed = 'index-build-failed',
  IndexBuildRequested = 'index-build-requested',
  ItemCreated = 'item-created',
  ItemUpdated = 'item-updated',
  ItemDeleted = 'item-deleted',
  ItemRestored = 'item-restored',
  IdentityCreated = 'identity-created',
  IdentityUpdated = 'identity-updated',
  IdentityDeleted = 'identity-deleted',
  IdentityRegistered = 'identity-registered',
  IdentityLoggedIn = 'identity-logged-in',
  IdentityLoggedOut = 'identity-logged-out',
  IdentityUpdatedSelf = 'identity-updated-self',
  IdentityDeletedSelf = 'identity-deleted-self',
  IdentitySessionsRevoked = 'identity-sessions-revoked',
  IdentityPasswordResetRequested = 'identity-password-reset-requested',
  IdentityPasswordReset = 'identity-password-reset',
  IdentityEmailVerificationRequested = 'identity-email-verification-requested',
  IdentityEmailVerified = 'identity-email-verified',
  IdentityProviderLinked = 'identity-provider-linked',
  IdentityProviderUnlinked = 'identity-provider-unlinked',
  IdentityGroupCreated = 'identity-group-created',
  IdentityGroupUpdated = 'identity-group-updated',
  IdentityGroupDeleted = 'identity-group-deleted',
  IdentityGroupProviderUpdated = 'identity-group-provider-updated',
  IdentityGroupProviderDeleted = 'identity-group-provider-deleted',
  IdentityGroupWebhookDelivered = 'identity-group-webhook-delivered',
  IdentityGroupWebhookFailed = 'identity-group-webhook-failed',
  SettingUpdated = 'setting-updated',
  SubscriptionCreated = 'subscription-created',
  SubscriptionUpdated = 'subscription-updated',
  SubscriptionDeleted = 'subscription-deleted',
  SubscriptionSubscribed = 'subscription-subscribed',
  SubscriptionCancelled = 'subscription-cancelled',
  SubscriptionPaymentSucceeded = 'subscription-payment-succeeded',
  SubscriptionPaymentFailed = 'subscription-payment-failed',
}

export const EventTypeQueryParam = {
  encode: (value?: EventType | null | undefined): string | undefined =>
    value ? (value as string) : undefined,

  decode: (
    value?: string | (string | null | undefined)[] | null | undefined
  ): EventType | undefined =>
    value && Object.values(EventType).includes(value as any)
      ? (value as EventType)
      : undefined,
};

export type Event = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  user?: User;
  modelId: string;
  stream: EventStream;
  type: EventType;
  version?: string;
  currentVersion?: boolean;

  // Only present when requested via includeSnapshot / includeAttachments
  snapshot?: Record<string, any> | null;
  attachments?: Record<string, any> | null;
};

export const eventOrderFields = [
  'createdAt',
  'userId',
  'modelId',
  'stream',
  'type',
] as const;

export type EventOrderField = (typeof eventOrderFields)[number];
